import { supabase } from "../lib/supabase";
import type { FilterGroup } from "../types/filter";
import { getActiveWorkerFilterOptions } from "./workers";
import { getAllTeams } from "./teams";
import { getContractCategories } from "./contract-categories";
import { getContractCompanies } from "./contract-companies";
import { getContractTypesByCategory } from "./contract-types";

export interface GeoJSONFeature {
  type: "Feature";
  geometry: {
    type: "Point";
    coordinates: [number, number]; // [longitude, latitude]
  };
  properties: {
    customer_count: number;
    total_commission?: number;
    avg_customer_age?: number | null;
  };
}

export interface GeoJSONFeatureCollection {
  type: "FeatureCollection";
  features: GeoJSONFeature[];
}

/**
 * One 5-digit PLZ area aggregated by `get_customer_age_by_plz`.
 *
 * This is TABULAR data keyed by zip_code, not GeoJSON: the geometry lives in a
 * static boundary file and is joined client-side via mergePlzGeometry(). The
 * age metric aggregates to PLZ level because per-address age dots were
 * statistically meaningless (71.7% represented a single customer).
 */
export interface PlzAgeArea {
  zip_code: string;
  customer_count: number;
  median_age: number;
  avg_age: number;
  /** [longitude, latitude] mean of the member addresses. */
  centroid: [number, number];
  /** false when customer_count < 15 - rendered neutral grey, not coloured. */
  is_reliable: boolean;
}

export interface PlzAgeCollection {
  type: "PlzAgeCollection";
  areas: PlzAgeArea[];
}

/** A PLZ boundary polygon joined to its age aggregate, ready for Mapbox. */
export interface PlzAgeFeature {
  type: "Feature";
  geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon;
  properties: {
    zip_code: string;
    customer_count: number;
    median_age: number;
    is_reliable: boolean;
  };
}

export interface PlzAgeFeatureCollection {
  type: "FeatureCollection";
  features: PlzAgeFeature[];
}

// In-memory cache to prevent redundant fetches
interface CacheEntry {
  data: GeoJSONFeatureCollection;
  timestamp: number;
}

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes TTL
const cache = new Map<string, CacheEntry>();
const inFlightRequests = new Map<string, Promise<GeoJSONFeatureCollection>>();

// PLZ age aggregates need their own cache: CacheEntry.data is typed as
// GeoJSONFeatureCollection and this payload is a different shape entirely.
interface PlzCacheEntry {
  data: PlzAgeCollection;
  timestamp: number;
}
const plzCache = new Map<string, PlzCacheEntry>();
const plzInFlight = new Map<string, Promise<PlzAgeCollection>>();

export function clearHeatmapCache(): void {
  cache.clear();
  plzCache.clear();
}

/**
 * Fetches customer locations formatted as GeoJSON using the advanced Condition Builder filter tree.
 * 
 * Performance & Execution:
 * 1. Calls server-side PostgreSQL RPC `get_customer_locations_filtered` with the dynamic condition tree.
 * 2. Caches results based on the serialized filter tree with a 5-minute TTL.
 * 3. Surfaces RPC errors so a denied request cannot retry through a broader endpoint.
 */
export async function getCustomerLocationsWithFilter(
  filterConfig: FilterGroup | null
): Promise<GeoJSONFeatureCollection> {
  const cacheKey = filterConfig ? `filtered:${JSON.stringify(filterConfig)}` : "__no_filter__";

  const cached = cache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.data;
  }

  const existingRequest = inFlightRequests.get(cacheKey);
  if (existingRequest) {
    return existingRequest;
  }

  const fetchPromise = (async () => {
    try {
      // 1. Try new filtered RPC
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)(
        "get_customer_locations_filtered",
        { p_filter: filterConfig }
      );

      if (!error && data && Array.isArray(data.features)) {
        const result: GeoJSONFeatureCollection = {
          type: "FeatureCollection",
          features: data.features,
        };
        cache.set(cacheKey, { data: result, timestamp: Date.now() });
        return result;
      }

      if (error) throw error;
      throw new Error("Invalid heatmap response");
    } finally {
      inFlightRequests.delete(cacheKey);
    }
  })();

  inFlightRequests.set(cacheKey, fetchPromise);
  return fetchPromise;
}

/** Minimum valid-age customers before a PLZ is coloured rather than greyed out. */
export const PLZ_RELIABILITY_THRESHOLD = 15;

const PLZ_BOUNDARY_URL = "/geo/plz-5stellig.min.geojson";

// The boundary file is static and rarely changes, so it is fetched at most once
// per page load and kept in module scope. It is deliberately NOT imported: the
// build has sourcemaps + a Sentry upload, and inlining it would inflate both.
let plzBoundaryPromise: Promise<GeoJSON.FeatureCollection> | null = null;

/**
 * Lazily loads the simplified PLZ boundary polygons from /public.
 * Resolves to an empty FeatureCollection if the asset is missing, so the app
 * degrades to "no polygons" rather than breaking the whole heatmap page.
 */
export async function loadPlzBoundaries(): Promise<GeoJSON.FeatureCollection> {
  if (plzBoundaryPromise) return plzBoundaryPromise;

  plzBoundaryPromise = (async () => {
    try {
      const res = await fetch(PLZ_BOUNDARY_URL);
      if (!res.ok) {
        console.warn(
          `PLZ boundary file not available (HTTP ${res.status}). ` +
            "The Kundenalter map will render without polygons."
        );
        return { type: "FeatureCollection", features: [] } as GeoJSON.FeatureCollection;
      }
      return (await res.json()) as GeoJSON.FeatureCollection;
    } catch (err) {
      console.warn("Failed to load PLZ boundaries:", err);
      plzBoundaryPromise = null; // allow a retry on the next attempt
      return { type: "FeatureCollection", features: [] } as GeoJSON.FeatureCollection;
    }
  })();

  return plzBoundaryPromise;
}

/**
 * Reads the PLZ code from a boundary feature. Different published datasets name
 * this property differently, so accept the common variants.
 */
function readBoundaryPlz(props: Record<string, unknown> | null): string | null {
  if (!props) return null;
  const raw = props["plz"] ?? props["postcode"] ?? props["zip_code"] ?? props["PLZ"];
  if (raw == null) return null;
  // Coerce carefully: a JSON number would drop the leading zero of e.g. "01067".
  return typeof raw === "number" ? String(raw).padStart(5, "0") : String(raw);
}

/**
 * Joins PLZ age aggregates onto their boundary polygons by zip_code string.
 * Boundaries with no matching data are dropped, so only canvassed areas render.
 */
export function mergePlzGeometry(
  areas: PlzAgeArea[],
  boundaries: GeoJSON.FeatureCollection
): PlzAgeFeatureCollection {
  const byPlz = new Map<string, PlzAgeArea>();
  for (const area of areas) byPlz.set(area.zip_code, area);

  const features: PlzAgeFeature[] = [];

  for (const feature of boundaries.features) {
    const plz = readBoundaryPlz(feature.properties as Record<string, unknown> | null);
    if (!plz) continue;

    const area = byPlz.get(plz);
    if (!area) continue; // no customers here - do not render the polygon at all

    const geometry = feature.geometry;
    if (!geometry || (geometry.type !== "Polygon" && geometry.type !== "MultiPolygon")) {
      continue;
    }

    features.push({
      type: "Feature",
      geometry,
      properties: {
        zip_code: area.zip_code,
        customer_count: area.customer_count,
        median_age: area.median_age,
        is_reliable: area.is_reliable,
      },
    });
  }

  return { type: "FeatureCollection", features };
}

/**
 * Fetches median customer age aggregated per 5-digit PLZ, honouring the same
 * Condition Builder filter tree as the point-based metrics.
 *
 * Ages <= 18 (sales-rep placeholder birthdates) and > 100 are excluded
 * server-side before aggregation - see the get_customer_age_by_plz migration.
 */
export async function getCustomerAgeByPlz(
  filterConfig: FilterGroup | null
): Promise<PlzAgeCollection> {
  const cacheKey = filterConfig
    ? `plz-age:${JSON.stringify(filterConfig)}`
    : "plz-age:__no_filter__";

  const cached = plzCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.data;
  }

  const existingRequest = plzInFlight.get(cacheKey);
  if (existingRequest) {
    return existingRequest;
  }

  const fetchPromise = (async () => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)(
        "get_customer_age_by_plz",
        { p_filter: filterConfig }
      );

      if (!error && data && Array.isArray(data.areas)) {
        const result: PlzAgeCollection = {
          type: "PlzAgeCollection",
          areas: data.areas as PlzAgeArea[],
        };
        plzCache.set(cacheKey, { data: result, timestamp: Date.now() });
        return result;
      }

      if (error) {
        console.warn(
          "get_customer_age_by_plz not available or failed:",
          error.message
        );
      }

      return { type: "PlzAgeCollection", areas: [] } as PlzAgeCollection;
    } finally {
      plzInFlight.delete(cacheKey);
    }
  })();

  plzInFlight.set(cacheKey, fetchPromise);
  return fetchPromise;
}

/**
 * Convenience wrapper: fetches the PLZ aggregates and their boundary polygons in
 * parallel and returns a ready-to-render FeatureCollection.
 */
export async function getPlzAgeChoropleth(
  filterConfig: FilterGroup | null
): Promise<PlzAgeFeatureCollection> {
  const [ageData, boundaries] = await Promise.all([
    getCustomerAgeByPlz(filterConfig),
    loadPlzBoundaries(),
  ]);
  return mergePlzGeometry(ageData.areas, boundaries);
}

/**
 * Pre-fetches all filter option metadata in parallel (workers, teams, categories, companies, contract types)
 * for snappy and responsive condition builder interactions.
 */
export async function fetchFilterMetadata() {
  const [workers, teams, categories, companies, contractTypes] = await Promise.all([
    getActiveWorkerFilterOptions().catch((err) => {
      console.warn("Failed to load workers for filter metadata:", err);
      return [];
    }),
    getAllTeams().catch((err) => {
      console.warn("Failed to load teams for filter metadata:", err);
      return [];
    }),
    getContractCategories().catch((err) => {
      console.warn("Failed to load categories for filter metadata:", err);
      return [];
    }),
    getContractCompanies().catch((err) => {
      console.warn("Failed to load companies for filter metadata:", err);
      return [];
    }),
    getContractTypesByCategory("all").catch((err) => {
      console.warn("Failed to load contract types for filter metadata:", err);
      return [];
    }),
  ]);

  return { workers, teams, categories, companies, contractTypes };
}
